import React from 'react';
import { Link } from 'react-router-dom';
import { FaExclamationTriangle, FaSync, FaEnvelope, FaHome } from 'react-icons/fa';
import styled from 'styled-components';

const PRIMARY_COLOR = '#182848';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fffdf5, #fff8e1);
  position: relative;
  padding: 1rem;
  box-sizing: border-box;
`;

const BlobBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 70%;
  height: 100%;
  background: linear-gradient(135deg, #fff3cd, #ffeaa7);
  clip-path: polygon(50% 0%, 100% 0, 100% 100%, 0% 100%);
  z-index: -1;
  opacity: 0.6;

  @media (max-width: 768px) {
    width: 100%;
    opacity: 0.4;
  }
`;

const AlertCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.14);
  padding: 2.5rem;
  max-width: 600px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
  border: 1px solid #e0e4ea;

  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 576px) {
    padding: 2rem 1.5rem;
  }
`;

const WarningIcon = styled(FaExclamationTriangle)`
  font-size: 4rem;
  color: #ffc107;
  margin-bottom: 1rem;

  @media (max-width: 576px) {
    font-size: 3.5rem;
  }
`;

const Title = styled.h1`
  font-size: 2.3rem;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  font-weight: 700;

  @media (max-width: 576px) {
    font-size: 1.9rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 1.8rem;

  @media (max-width: 576px) {
    font-size: 1rem;
  }
`;

const InfoText = styled.div`
  background: #fff3cd;
  border-radius: 10px;
  padding: 1rem;
  margin: 1.5rem 0;
  font-size: 0.95rem;
  color: #856404;
  text-align: left;
  border: 1px solid #ffeaa7;
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const StyledButton = styled.button`
  background-color: ${PRIMARY_COLOR};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #132038;
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(24, 40, 72, 0.2);
  }

  &.outline {
    background-color: transparent;
    color: ${PRIMARY_COLOR};
    border: 2px solid ${PRIMARY_COLOR};

    &:hover {
      background-color: rgba(24, 40, 72, 0.1);
    }
  }
`;

const ContactLink = styled.a`
  color: ${PRIMARY_COLOR};
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 1.2rem;
  font-size: 0.95rem;

  &:hover {
    text-decoration: underline;
  }
`;

const PaymentPendingPage = () => {
  return (
    <PageWrapper>
      <BlobBackground />
      <AlertCard>
        <WarningIcon aria-hidden="true" />
        <Title>Payment Pending</Title>
        <Subtitle>
          We're still verifying your payment. This may take a few moments.
        </Subtitle>

        <InfoText>
          <strong>Note:</strong> If you were charged but still see this message, do not retry.
          We’ll confirm your payment shortly. You can refresh or check back later.
        </InfoText>

        <ActionGroup>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <StyledButton type="button">
              <FaHome /> Go Home
            </StyledButton>
          </Link>

          <Link to="/payment-response" style={{ textDecoration: 'none' }}>
            <StyledButton type="button" className="outline">
              <FaSync /> Refresh Status
            </StyledButton>
          </Link>
        </ActionGroup>

        <ContactLink href="mailto:info@q2d.in?subject=Payment Pending - Need Help">
          <FaEnvelope /> Contact Support
        </ContactLink>
      </AlertCard>
    </PageWrapper>
  );
};

export default PaymentPendingPage;